import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Image as ImageIcon,
  Lock,
  Sparkles,
  MapPin,
  Layers,
  Home,
  Building2,
  Ruler,
  CheckCircle,
  Clock,
  Info,
  Map as MapIcon,
} from 'lucide-react';
import { OutlineButton, PrimaryButton } from '../../components/Buttons';
import axiosInstance from '../../services/api/axios';
import { fetchPropertyConfigurations } from '../../services/projects';
import { toast } from 'react-toastify';
import { useUser } from '../../context/UserContext';
import { normalizePropertyConfigMediaForUI } from '../../utils/media';
import ImageCarousel from '../../components/ImageCarousel';

// Tabs available on this page. Hash-driven so the URL reflects the active section.
// Designs tab was intentionally removed from this view — designs live on a
// separate ID page now.
const TAB_IDS = ['overview', 'properties', 'images', 'floorplans'];
const parseTabFromHash = (hash) => {
  if (!hash) return 'overview';
  const cleaned = hash.replace(/^#/, '').toLowerCase();
  return TAB_IDS.includes(cleaned) ? cleaned : 'overview';
};

const InteriorDesignerConfigDetails = () => {
  const { projectId, configId } = useParams();
  const navigate = useNavigate();
  const navLocation = useLocation();
  const { userInfo } = useUser();

  // Get config and project data from navigation state if available
  const passedConfigData = navLocation.state?.configData;
  const passedProjectData = navLocation.state?.projectData;
  const passedPhaseId = navLocation.state?.phaseId;
  const [config, setConfig] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() =>
    typeof window !== 'undefined' ? parseTabFromHash(window.location.hash) : 'overview'
  );
  const [mediaImages, setMediaImages] = useState([]);
  const [floorPlanImages, setFloorPlanImages] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propertyCount, setPropertyCount] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState('none'); // 'none', 'pending', 'paid'
  const [phaseId, setPhaseId] = useState(null);

  // Keep activeTab in sync with the URL hash (back/forward, deep links).
  useEffect(() => {
    setActiveTab(parseTabFromHash(navLocation.hash));
  }, [navLocation.hash]);

  const selectTab = (tabId) => {
    setActiveTab(tabId);
    if (typeof window !== 'undefined') {
      navigate(`${navLocation.pathname}#${tabId}`, { replace: true });
    }
  };

  useEffect(() => {
    const fetchConfigDetails = async () => {
      try {
        setLoading(true);

        // Use passed config data if available, otherwise fetch
        let configData = passedConfigData;
        let projectData = passedProjectData;
        let extractedPhaseId = passedPhaseId;

        if (!configData) {
          // Fetch configurations if not passed
          const configResponse = await fetchPropertyConfigurations({
            projectId: projectId,
            customerId: userInfo?.loginIdentifierDetails?.customer?.customerId,
          });

          let configsArray = [];
          if (configResponse?.items && Array.isArray(configResponse.items)) {
            configsArray = configResponse.items;
          } else if (Array.isArray(configResponse)) {
            configsArray = configResponse;
          }

          configData = configsArray.find(
            c => (c.propertyConfigId || c.id) == configId
          );

          if (!configData) {
            toast.error('Configuration not found');
            navigate(`/interior-designer/phases/${projectId}`);
            return;
          }

          // Extract phaseId from config if available (check both direct and nested)
          extractedPhaseId = configData.projectPhaseId || configData.projectPhase?.projectPhaseId;

          // Extract project data from config response (nested in projectPhase.project)
          if (configData.projectPhase?.project) {
            projectData = configData.projectPhase.project;
          }
        }

        // Fallback: If project not found and not passed, fetch separately
        if (!projectData) {
          console.warn('Project not found, fetching separately...');
          try {
            const projectResponse = await axiosInstance.get(`/projects`, {
              params: {
                projectId,
                customerId: null,
              },
            });

            if (projectResponse?.items && Array.isArray(projectResponse.items) && projectResponse.items.length > 0) {
              projectData = projectResponse.items[0];
            } else if (Array.isArray(projectResponse) && projectResponse.length > 0) {
              projectData = projectResponse[0];
            }
          } catch (error) {
            console.error('Error fetching project details:', error);
          }
        }

        setConfig(configData);
        setProject(projectData);

        if (extractedPhaseId) {
          setPhaseId(extractedPhaseId);
        }

        const resolvedConfigId = configData.propertyConfigId || configData.id;

        // Fetch config images (gallery + floor plans, split via util)
        try {
          const imagesResponse = await axiosInstance.get('/property/config/media', {
            params: {
              property_config_id: resolvedConfigId,
            },
          });

          const { gallery, detailedFloorPlans } = normalizePropertyConfigMediaForUI(imagesResponse);
          setMediaImages(gallery);
          setFloorPlanImages(detailedFloorPlans);
        } catch (error) {
          console.error('Error fetching config images:', error);
          setMediaImages([]);
          setFloorPlanImages([]);
        }

        // Fetch properties for this config (powers the Properties tab + count)
        try {
          const propertiesResponse = await axiosInstance.get('/property', {
            params: {
              projectId: projectId,
              propertyConfigId: resolvedConfigId,
            },
          });

          let propertiesArray = [];
          if (propertiesResponse?.items && Array.isArray(propertiesResponse.items)) {
            propertiesArray = propertiesResponse.items;
          } else if (Array.isArray(propertiesResponse)) {
            propertiesArray = propertiesResponse;
          }

          setProperties(propertiesArray);
          // Prefer explicit list length; fall back to propertiesId on the config if present.
          if (propertiesArray.length > 0) {
            setPropertyCount(propertiesArray.length);
          } else if (Array.isArray(configData.propertiesId)) {
            setPropertyCount(configData.propertiesId.length);
          } else {
            setPropertyCount(0);
          }
        } catch (error) {
          console.error('Error fetching properties for config:', error);
          setProperties([]);
          // Fall back to propertiesId length so the hero pill still shows a number.
          if (Array.isArray(configData.propertiesId)) {
            setPropertyCount(configData.propertiesId.length);
          } else {
            setPropertyCount(0);
          }
        }
      } catch (error) {
        console.error('Error fetching config details:', error);
        toast.error('Failed to load configuration details');
        navigate(`/interior-designer/phases/${projectId}`);
      } finally {
        setLoading(false);
      }
    };

    if (projectId && configId) {
      fetchConfigDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, configId]); // Only depend on projectId and configId, not passed data to avoid refetching

  // Check subscription status for the phase
  useEffect(() => {
    const checkSubscription = async () => {
      if (!phaseId || !userInfo?.loginId) return;

      try {
        const response = await axiosInstance.get(`/interior-designers/phase-subscriptions`);

        let subscriptions = [];
        if (response?.items && Array.isArray(response.items)) {
          subscriptions = response.items;
        } else if (Array.isArray(response)) {
          subscriptions = response;
        }

        // Find subscription for this phase
        const phaseSubscription = subscriptions.find(
          sub => sub.projectPhase?.projectPhaseId == phaseId || sub.projectPhaseId == phaseId
        );

        if (phaseSubscription) {
          const paymentStatus = phaseSubscription.paymentStatus?.toUpperCase();
          if (paymentStatus === 'PAID') {
            setSubscriptionStatus('paid');
          } else if (paymentStatus === 'PENDING') {
            setSubscriptionStatus('pending');
          } else {
            setSubscriptionStatus('none');
          }
        } else {
          setSubscriptionStatus('none');
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        setSubscriptionStatus('none');
      }
    };

    if (phaseId && userInfo?.loginId) {
      checkSubscription();
    }
  }, [phaseId, userInfo]);

  const handleBackClick = () => {
    // Try to get phaseId from config if not set in state
    const currentPhaseId = phaseId || config?.projectPhaseId || config?.projectPhase?.projectPhaseId;

    if (currentPhaseId && projectId) {
      navigate(`/interior-designer/phases/${projectId}/${currentPhaseId}`);
    } else if (projectId) {
      // Fallback: navigate to phases list if phaseId not available
      navigate('/interior-designer/phases');
    } else {
      navigate('/interior-designer/phases');
    }
  };

  const handleSubscribeClick = () => {
    if (phaseId && projectId) {
      navigate(`/interior-designer/phases/${projectId}/${phaseId}`);
    }
  };

  // Subscription pill (small) for the hero status row.
  const renderSubscriptionPill = (status) => {
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
          <CheckCircle size={12} />
          Unlocked
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
          <Clock size={12} />
          Subscription pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
        <Lock size={12} />
        Locked
      </span>
    );
  };

  // Slate-language skeleton loader.
  const SkeletonLoader = () => (
    <div className="px-4 sm:px-6 py-6 space-y-6">
      <div className="h-4 w-32 animate-pulse rounded bg-slate-100" />
      <div className="h-48 animate-pulse rounded-2xl bg-slate-100" />
      <div className="h-12 w-72 animate-pulse rounded-2xl bg-slate-100" />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
      <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-48 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
    </div>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!config || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Building2 className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">Configuration not found</p>
          <p className="mt-1 text-sm text-slate-500">
            The configuration you&rsquo;re looking for is unavailable.
          </p>
          <OutlineButton onClick={handleBackClick} className="mt-4">
            Back to Phases
          </OutlineButton>
        </div>
      </div>
    );
  }

  const configName = config.configName || config.propertyConfig?.configName || 'Unnamed Config';
  const cityState = project.address
    ? `${project.address.city || ''}, ${project.address.state || ''}`.replace(/^,\s*|,\s*$/g, '')
    : 'Location not available';
  const fullAddress = (() => {
    if (!project.address) return cityState;
    const a = project.address;
    const parts = [
      [a.buildingNo, a.streetName1].filter(Boolean).join(' '),
      a.streetName2,
      a.district,
      a.city,
      a.state,
      a.pinCode,
    ].filter((s) => s && String(s).trim() !== '');
    return parts.length ? parts.join(', ') : cityState;
  })();

  // Format area helpers — kept identical fallback chain as the previous file.
  const formatArea = (sqft, sqmt, fallbackField) => {
    if (typeof sqft === 'number') return `${sqft.toFixed(2)} sq.ft`;
    if (typeof sqmt === 'number') return `${sqmt.toFixed(2)} sq.mt`;
    if (fallbackField !== undefined && fallbackField !== null && fallbackField !== '')
      return `${fallbackField} sq.ft`;
    return 'N/A';
  };

  const carpetAreaText = formatArea(config.carpetAreaSqft, config.carpetAreaSqmt, config.carpetArea);
  const superBuiltupText = formatArea(config.superBuiltupAreaSqft, config.superBuiltupAreaSqmt);
  const balconyText = formatArea(config.balconyAreaSqft, config.balconyAreaSqmt);
  const dryBalconyText = formatArea(config.dryBalconyAreaSqft, config.dryBalconyAreaSqmt);

  const totalMediaFiles = mediaImages.length + floorPlanImages.length;

  // Stat tiles shown in Overview.
  const statTiles = [
    {
      label: 'Total properties',
      value: propertyCount,
      icon: Home,
    },
    {
      label: 'Images',
      value: mediaImages.length,
      icon: ImageIcon,
    },
    {
      label: 'Floor plans',
      value: floorPlanImages.length,
      icon: MapIcon,
    },
    {
      label: 'Size',
      value: config.sizeBhk ? `${config.sizeBhk} BHK` : '—',
      icon: Layers,
    },
  ];

  // Tab metadata — drives the pill nav.
  const TABS = [
    { id: 'overview', label: 'Overview', icon: Info },
    { id: 'properties', label: 'Properties', icon: Home, count: propertyCount },
    { id: 'images', label: 'Images', icon: ImageIcon, count: mediaImages.length },
    { id: 'floorplans', label: 'Floor plans', icon: MapIcon, count: floorPlanImages.length },
  ];

  return (
    <div className="px-4 sm:px-6 py-6 space-y-6">
      {/* Back link above the hero */}
      <button
        type="button"
        onClick={handleBackClick}
        className="inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-flamingo-600"
      >
        <ArrowLeft size={14} />
        Back to phase
      </button>

      {/* Hero band — slate gradient with radial overlay */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/40 via-slate-800/30 to-transparent" />

        <div className="relative px-6 py-7 sm:px-8 sm:py-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
            <Sparkles size={12} />
            Interior Designer &middot; Configuration
          </div>

          <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">
            {configName}
          </h1>

          {(project.projectName || project.developer) && (
            <p className="mt-1 text-sm text-white/80">
              {project.projectName || project.developer}
              {project.projectName && project.developer ? (
                <span className="text-white/60"> &middot; {project.developer}</span>
              ) : null}
            </p>
          )}

          <div className="mt-2 flex items-start gap-1.5 text-xs text-white/70">
            <MapPin size={13} className="mt-0.5 shrink-0" />
            <span className="line-clamp-2">{fullAddress}</span>
          </div>

          {/* Stat pills row */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {phaseId && renderSubscriptionPill(subscriptionStatus)}
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
              <Home size={12} />
              {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
              <ImageIcon size={12} />
              {totalMediaFiles} {totalMediaFiles === 1 ? 'media file' : 'media files'}
            </span>
            {config.propertyType && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
                <Building2 size={12} />
                {config.propertyType}
              </span>
            )}
            {config.sizeBhk && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
                <Layers size={12} />
                {config.sizeBhk} BHK
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Subscription banner — only when there's a phaseId and not yet paid */}
      {phaseId && subscriptionStatus === 'none' && (
        <div className="rounded-2xl border border-flamingo-200 bg-gradient-to-r from-amber-50 to-flamingo-50 p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-flamingo-500 text-white">
                <Lock size={18} />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">Unlock this phase</h2>
                <p className="mt-0.5 text-sm text-slate-600">
                  Subscribe to access full property details, plans and the ability to upload your own designs.
                </p>
              </div>
            </div>
            <PrimaryButton
              onClick={handleSubscribeClick}
              className="!w-auto !px-4 !py-2 text-sm whitespace-nowrap"
            >
              <span className="inline-flex items-center gap-1.5">
                <Sparkles size={14} />
                Subscribe to phase
              </span>
            </PrimaryButton>
          </div>
        </div>
      )}

      {phaseId && subscriptionStatus === 'pending' && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white">
              <Clock size={18} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Subscription pending</h2>
              <p className="mt-0.5 text-sm text-slate-600">
                Your subscription request is awaiting approval. You&rsquo;ll be notified when it&rsquo;s active.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pill tab nav */}
      <div className="inline-flex flex-wrap gap-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
        {TABS.map(({ id, label, icon: Icon, count }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => selectTab(id)}
              className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? 'bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <Icon size={14} />
              {label}
              {typeof count === 'number' && count > 0 && (
                <span
                  className={`tabular-nums rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                    active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab panels */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stat tiles */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-slate-900">At a glance</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Quick snapshot of this configuration.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {statTiles.map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-xl border border-slate-100 bg-slate-50/40 p-4">
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                    <Icon size={13} className="text-slate-400" />
                    {label}
                  </div>
                  <div className="mt-1.5 text-2xl font-semibold text-slate-900 tabular-nums">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* About + key facts */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:col-span-2">
              <h2 className="text-lg font-semibold text-slate-900">About this configuration</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {project.developer ? `${project.developer} · ` : ''}
                {configName}
              </p>

              {config.description?.trim() ? (
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  {config.description.trim()}
                </p>
              ) : (
                <p className="mt-4 text-sm italic text-slate-400">No description provided.</p>
              )}

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 border-t border-slate-100 pt-5 text-sm sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Configuration name
                  </p>
                  <p className="mt-1 font-medium text-slate-900">
                    {config.configName || configName || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Property type
                  </p>
                  <p className="mt-1 font-medium text-slate-900">
                    {config.propertyType || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Size (BHK)
                  </p>
                  <p className="mt-1 font-medium text-slate-900">
                    {config.sizeBhk ? `${config.sizeBhk} BHK` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Base price
                  </p>
                  <p className="mt-1 font-semibold text-flamingo-600 tabular-nums">
                    {config.basePrice ? `₹${Number(config.basePrice).toLocaleString('en-IN')}` : '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Area breakdown */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">Area breakdown</h2>
              <p className="mt-0.5 text-sm text-slate-500">Measured area for this configuration.</p>

              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/40 px-3 py-2.5">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Ruler size={13} className="text-slate-400" />
                    Carpet area
                  </div>
                  <span className="font-medium text-slate-900 tabular-nums">{carpetAreaText}</span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/40 px-3 py-2.5">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Ruler size={13} className="text-slate-400" />
                    Super built-up
                  </div>
                  <span className="font-medium text-slate-900 tabular-nums">{superBuiltupText}</span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/40 px-3 py-2.5">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Ruler size={13} className="text-slate-400" />
                    Balcony
                  </div>
                  <span className="font-medium text-slate-900 tabular-nums">{balconyText}</span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/40 px-3 py-2.5">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Ruler size={13} className="text-slate-400" />
                    Dry balcony
                  </div>
                  <span className="font-medium text-slate-900 tabular-nums">{dryBalconyText}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'properties' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Properties</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {propertyCount === 0
                  ? 'No properties linked to this configuration yet.'
                  : `${propertyCount} ${propertyCount === 1 ? 'property' : 'properties'} in this configuration`}
              </p>
            </div>
            {propertyCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <Home size={12} />
                {propertyCount}
              </span>
            )}
          </div>

          {properties.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Home className="text-slate-400" size={22} />
              </div>
              <p className="mt-3 text-base font-medium text-slate-800">No property details available</p>
              <p className="mt-1 text-sm text-slate-500">
                Properties will appear here once they are added to this configuration.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {properties.map((p, idx) => {
                const pid = p.propertyId || p.id || idx;
                const label =
                  p.propertyNumber ||
                  p.unitNumber ||
                  p.flatNumber ||
                  p.name ||
                  `Property ${idx + 1}`;
                const floor =
                  p.floorNumber !== undefined && p.floorNumber !== null && p.floorNumber !== ''
                    ? `Floor ${p.floorNumber}`
                    : null;
                const tower = p.tower || p.towerName || p.buildingName;
                const status = p.propertyStatus || p.status;
                return (
                  <div
                    key={pid}
                    className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-slate-300"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-flamingo-50 text-flamingo-600">
                          <Home size={16} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{label}</p>
                          {tower && (
                            <p className="text-xs text-slate-500">{tower}</p>
                          )}
                        </div>
                      </div>
                      {status && (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                          {status}
                        </span>
                      )}
                    </div>
                    {(floor || p.propertyType) && (
                      <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        {floor && (
                          <span className="inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-slate-100">
                            {floor}
                          </span>
                        )}
                        {p.propertyType && (
                          <span className="inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-slate-100">
                            {p.propertyType}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'images' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Images</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {mediaImages.length === 0
                  ? 'No images uploaded for this configuration yet.'
                  : `${mediaImages.length} ${mediaImages.length === 1 ? 'image' : 'images'} available`}
              </p>
            </div>
            {mediaImages.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <ImageIcon size={12} />
                {mediaImages.length}
              </span>
            )}
          </div>

          {mediaImages.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <ImageIcon className="text-slate-400" size={22} />
              </div>
              <p className="mt-3 text-base font-medium text-slate-800">No images available</p>
              <p className="mt-1 text-sm text-slate-500">
                Photos and renders will appear here once uploaded.
              </p>
            </div>
          ) : (
            <ImageCarousel images={mediaImages} />
          )}
        </div>
      )}

      {activeTab === 'floorplans' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Floor plans</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {floorPlanImages.length === 0
                  ? 'No floor plans uploaded for this configuration yet.'
                  : `${floorPlanImages.length} ${floorPlanImages.length === 1 ? 'plan' : 'plans'} available`}
              </p>
            </div>
            {floorPlanImages.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <MapIcon size={12} />
                {floorPlanImages.length}
              </span>
            )}
          </div>

          {floorPlanImages.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <MapIcon className="text-slate-400" size={22} />
              </div>
              <p className="mt-3 text-base font-medium text-slate-800">No floor plans available</p>
              <p className="mt-1 text-sm text-slate-500">
                Detailed floor plans will appear here once uploaded.
              </p>
            </div>
          ) : (
            <ImageCarousel images={floorPlanImages} />
          )}
        </div>
      )}
    </div>
  );
};

export default InteriorDesignerConfigDetails;
